/**
 * Google Tag Manager + GA4 DataLayer helpers.
 *
 * Every push is wrapped in try/catch so tracking can never break the app.
 * The GTM container (GTM-PKJQVVKG) and GA4 property (G-1JVEGP3SV3) are
 * configured externally — this module only pushes events to `window.dataLayer`.
 */

import { getClickIds } from "./clickIds";

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

// Module-level user props — lean segmentation fields auto-merged into every push.
// Only non-PII identifiers go here (no email, no name).
let userProps: Record<string, unknown> | null = null;

export type UserType =
  | "internal"
  | "chargebee"
  | "corporate"
  | "direct"
  | "unknown";

/**
 * Set user identity and push a `user_identified` event.
 *
 * Can be called twice per session:
 *   Phase 1 (initKeycloak.ts) — token-only data, fires immediately at login.
 *   Phase 2 (useUserIdentification hook) — enriched with subscription data
 *           for chargebee users once the query settles.
 *
 * `userProps` stores lean segmentation fields (user_id, access_type,
 * user_type) that are auto-merged into every subsequent dataLayer push.
 * Subscription details are only included in the `user_identified` event
 * itself — they are not merged into other events.
 */
export function identifyUser(params: {
  user_id: string;
  access_type: string;
  user_type: UserType;
  subscription_status?: string;
  subscription_plan?: string;
  subscription_age_days?: number | null;
  trial_days_remaining?: number | null;
  squawk_channels: string;
  has_app_pro: boolean;
  has_equities_suite: boolean;
  /** SHA-256 hash of lowercased email for enhanced conversion matching. Never the raw email. */
  email_sha256?: string;
}) {
  // Only lean segmentation fields go into userProps (merged into every event)
  userProps = {
    user_id: params.user_id,
    access_type: params.access_type,
    user_type: params.user_type,
  };

  // The full payload (including subscription details) goes into the event only
  push({ event: "user_identified", ...params });
}

/**
 * Clear user identity. Called on logout.
 */
export function clearUser() {
  userProps = null;
  seenOnce.clear();
}

function push(event: Record<string, unknown>) {
  try {
    window.dataLayer = window.dataLayer || [];
    // Click IDs are merged onto every push so sGTM can hand them off to
    // Meta CAPI / Google Ads / LinkedIn CAPI / Bing server-side without
    // each helper having to thread them through manually.
    window.dataLayer.push({
      ...event,
      ...getClickIds(),
      ...(userProps ?? {}),
    });
  } catch {
    // silently ignore — tracking must never break the app
  }
}

// ── Page view ────────────────────────────────────────────────────────
export function trackPageView(params: {
  page_path: string;
  page_title: string;
  // Attribution — included on the first page_view of a session
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_domain?: string;
  landing_page?: string;
}) {
  push({ event: "page_view", ...params });
}

// ── Auth events ──────────────────────────────────────────────────────
export function trackLogin(params: {
  method: string;
  user_id?: string;
  // Attribution fields — forwarded from stored session attribution
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  referrer_domain?: string;
  landing_page?: string;
}) {
  push({ event: "login", ...params });
}

export function trackLogout() {
  push({ event: "logout" });
}

// ── Signup intent (pricing modal opened / checkout CTA clicked) ──────
export function trackSignupIntent(params: {
  intent_type: "pricing_modal_opened" | "checkout_clicked" | "business_inquiry";
  plan_type?: string;
  plan_variant?: string;
  billing_cycle?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer_domain?: string;
  landing_page?: string;
  /** FirstPromoter referral token, so sGTM can relay affiliate conversions. */
  fp_ref?: string;
}) {
  push({ event: "signup_intent", ...params });
}

// ── Demo & paywall ───────────────────────────────────────────────────
//
// The marketing site records the decision to come and look (demo_cta_click).
// Nothing recorded arrival, so the two halves of the journey could not be
// joined and "does the demo persuade anyone" had no answer at all — the app
// emitted seventeen event types and every one of them was product usage.
//
// Views are deduplicated per session because they fire from components that
// re-render on every data tick; undeduplicated, paywall_view would out-count
// filter_applied within the hour and drown the signal it exists to carry.

const seenOnce = new Set<string>();

function pushOnce(key: string, event: Record<string, unknown>) {
  if (seenOnce.has(key)) return;
  seenOnce.add(key);
  push(event);
}

/** Where in the app a conversion prompt was shown or taken. */
export type ConversionSurface =
  | "demo_banner"
  | "premium_widget"
  | "widget_no_access"
  | "subscription_expired"
  | "subscription_lapsed"
  /** End of a completed guided-demo flow — the only CTA shown in context. */
  | "demo_guide";

/** The demo was actually seen, not merely clicked towards. Once per session. */
export function trackDemoView(params: { surface: ConversionSurface }) {
  pushOnce(`demo_view:${params.surface}`, { event: "demo_view", ...params });
}

/**
 * A locked widget was shown — the moment the product asks to be paid for.
 *
 * `authenticated` is carried rather than inferred downstream: a signed-in user
 * hitting a plan limit and an anonymous visitor hitting the demo wall are
 * different populations who convert differently, and the distinction is not
 * recoverable from user_type once the rows are aggregated.
 */
export function trackPaywallView(params: {
  surface: ConversionSurface;
  widget_name: string;
  authenticated: boolean;
}) {
  pushOnce(
    `paywall_view:${params.surface}:${params.widget_name}`,
    { event: "paywall_view", ...params },
  );
}

/**
 * An upgrade / "Try Pro" CTA was clicked.
 *
 * Deliberately the SAME event name the marketing site uses for its trial CTAs,
 * so the funnel spans both domains as one series rather than two that have to
 * be stitched by hand. `surface` here and hostName in GA4 keep the two sides
 * separable whenever they need reading apart.
 */
export function trackTrialCtaClick(params: {
  surface: ConversionSurface;
  widget_name?: string;
  authenticated?: boolean;
  /**
   * Guided-demo context: which flow produced the click, and which desk they
   * picked. Carried separately from widget_name because "the flow they
   * finished" and "the widget they were looking at" answer different
   * questions, and conflating them makes neither readable.
   */
  flow?: string;
  asset_class?: string;
}) {
  push({ event: "trial_cta_click", ...params });
}

// ── Guided demo progress ─────────────────────────────────────────────
//
// The two beats of the guided run that carry the funnel get their own event
// names rather than riding on feature_usage. Step drop-off is the question the
// demo exists to answer, and as a feature_usage row it can only be read by
// filtering on feature_name and then parsing an ordinal out of feature_detail
// — so the one series worth charting was the one that needed hand-assembly.
//
// Both events need their own tag in the GTM container (GTM-PKJQVVKG). A
// dataLayer push with no tag listening for it reaches nothing at all, silently
// — which is exactly how signup_intent went unrecorded for months.

/**
 * One guided-demo step completed, fired on the action that completes it.
 *
 * `step_id` is the stable key: all history is keyed on it, so a step may be
 * reordered (step_index moves with it) but must never be renamed — a rename
 * reads downstream as one step ending and a different one beginning.
 *
 * `step_total` travels with every row because it is not constant across runs:
 * the compare step is dropped for desks with no filtered wire, so a run is
 * three or four steps depending on the desk. Without it, "step 3" cannot be
 * told apart from "the last step".
 */
export function trackDemoStep(params: {
  step_id: string;
  step_index: number;
  step_total: number;
  demo_variant: string;
}) {
  push({ event: "demo_step", ...params });
}

/**
 * The guided run reached the end. Once per run.
 *
 * `steps_completed` counts steps actually done, not steps walked past — a run
 * finished by pressing Skip four times is a different outcome from one that
 * was followed, and if both report the same number the completion rate is a
 * measure of how many people found the Skip button.
 */
export function trackDemoComplete(params: {
  demo_variant: string;
  steps_completed: number;
}) {
  push({ event: "demo_complete", ...params });
}

// ── Content view ─────────────────────────────────────────────────────
export function trackContentView(params: {
  content_id: string | number;
  content_title: string;
  content_type: string;
  content_category?: string;
  content_source?: string;
  content_tags?: string;
  content_tickers?: string;
  content_widget?: string;
}) {
  push({ event: "content_view", ...params });
}

// ── Content engagement (time spent reading) ─────────────────────────
export function trackContentEngagement(params: {
  content_id: string | number;
  content_title: string;
  content_type: string;
  content_widget?: string;
  engagement_time_ms: number;
}) {
  push({ event: "content_engagement", ...params });
}

// ── Search ───────────────────────────────────────────────────────────
export function trackSearchSubmit(params: {
  search_term: string;
  search_context: string;
  search_filters?: string;
}) {
  push({ event: "search", ...params });
}

export function trackSearchResults(params: {
  search_term: string;
  search_context: string;
  results_count: number;
  search_filters?: string;
}) {
  push({ event: "search_results", ...params });
}

// ── Filter usage ────────────────────────────────────────────────────
export function trackFilterApplied(params: {
  filter_context: string;
  filter_type: string;
  filter_value: string;
  filter_count: number;
}) {
  push({ event: "filter_applied", ...params });
}

// ── Feature usage ────────────────────────────────────────────────────
type FeatureUsageParams = {
  feature_name: string;
  feature_action: string;
  feature_detail?: string;
};

type FeatureUsageListener = (params: FeatureUsageParams) => void;

const featureUsageListeners = new Set<FeatureUsageListener>();

/**
 * Observe feature_usage events in-app (used by the onboarding checklist to
 * passively detect task completion from already-instrumented actions).
 * Returns an unsubscribe function.
 */
export function onFeatureUsage(listener: FeatureUsageListener): () => void {
  featureUsageListeners.add(listener);
  return () => featureUsageListeners.delete(listener);
}

export function trackFeatureUsage(params: FeatureUsageParams) {
  push({ event: "feature_usage", ...params });
  // Listeners must never break tracking (or the calling feature).
  for (const listener of featureUsageListeners) {
    try {
      listener(params);
    } catch (e) {
      console.error("[gtm] feature_usage listener error:", e);
    }
  }
}

